import { curriculum } from '@/content/curriculum';
import type { LevelId, SchoolType } from '@/domain/ids';
import {
  learnerProfileSchema,
  type Avatar,
  type LearnerPreferences,
  type LearnerProfile,
} from '@/domain/records';
import { newId } from '@/lib/ids';
import { nowIso } from '@/lib/time';
import { db, safeWrite } from './database';
import { getActiveLearnerId, setActiveLearnerId } from './meta';

export interface NewLearnerInput {
  nickname?: string;
  avatar: Avatar;
  levelId: LevelId;
  schoolType: SchoolType;
  variantId: string;
  preferences: LearnerPreferences;
}

export const DEFAULT_PREFERENCES: LearnerPreferences = {
  locale: 'ar',
  soundEffects: true,
  speech: true,
  largeText: false,
  reducedMotion: false,
};

export async function listLearners(): Promise<LearnerProfile[]> {
  return db.learners.orderBy('createdAt').toArray();
}

export async function getLearner(id: string): Promise<LearnerProfile | undefined> {
  return db.learners.get(id);
}

/** Creates an anonymous profile: a random id, never a real identity. */
export async function createLearner(input: NewLearnerInput): Promise<LearnerProfile> {
  const now = nowIso();
  const nickname = input.nickname?.trim();
  const profile = learnerProfileSchema.parse({
    id: newId(),
    ...(nickname ? { nickname } : {}),
    avatar: input.avatar,
    levelId: input.levelId,
    schoolType: input.schoolType,
    variantId: input.variantId,
    curriculumVersionId: curriculum.versionId,
    preferences: input.preferences,
    createdAt: now,
    updatedAt: now,
  });
  await safeWrite(() => db.learners.add(profile));
  return profile;
}

export async function updateLearner(
  id: string,
  patch: Partial<Omit<LearnerProfile, 'id' | 'createdAt' | 'updatedAt'>>,
): Promise<LearnerProfile> {
  return safeWrite(() =>
    db.transaction('rw', db.learners, async () => {
      const current = await db.learners.get(id);
      if (!current) throw new Error('Learner not found');
      const { nickname, ...rest } = patch;
      const next = learnerProfileSchema.parse({
        ...current,
        ...rest,
        ...(nickname !== undefined ? { nickname: nickname.trim() || undefined } : {}),
        updatedAt: nowIso(),
      });
      if (!next.nickname) delete next.nickname;
      await db.learners.put(next);
      return next;
    }),
  );
}

/** Deletes a learner and every record that references them, atomically. */
export async function deleteLearner(id: string): Promise<void> {
  await safeWrite(() =>
    db.transaction(
      'rw',
      [db.learners, db.sessions, db.attempts, db.mastery, db.achievements, db.feedback, db.meta],
      async () => {
        await db.attempts.where('learnerId').equals(id).delete();
        await db.feedback.where('learnerId').equals(id).delete();
        await db.sessions.where('learnerId').equals(id).delete();
        await db.mastery.where('learnerId').equals(id).delete();
        await db.achievements.where('learnerId').equals(id).delete();
        await db.learners.delete(id);
        if ((await getActiveLearnerId()) === id) await setActiveLearnerId(undefined);
      },
    ),
  );
}

/** Removes every learner and all learning records from this device. Parent settings are kept unless asked. */
export async function deleteAllLearningData(
  options: { includeSettings?: boolean } = {},
): Promise<void> {
  await safeWrite(() =>
    db.transaction(
      'rw',
      [db.learners, db.sessions, db.attempts, db.mastery, db.achievements, db.feedback, db.meta],
      async () => {
        await Promise.all([
          db.feedback.clear(),
          db.attempts.clear(),
          db.sessions.clear(),
          db.mastery.clear(),
          db.achievements.clear(),
          db.learners.clear(),
        ]);
        if (options.includeSettings) await db.meta.clear();
        else await db.meta.delete('activeLearnerId');
      },
    ),
  );
}
